import { ArrowLeft } from 'lucide-react';
import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ROUTES } from '@/app/router/routes';
import type { Icon, ModuleAccent, SubModule } from '@/app/router/modules';
import { ModuleCard } from './ModuleCard';
import { ModuleGrid, ModuleGridItem } from './ModuleGrid';

/**
 * The middle level of School OS: one area of the school, and the things you
 * can do in it. Used for every module — the page is data, not a component per
 * module.
 */
export function ModuleDashboard({
  moduleKey,
  title,
  description,
  icon: IconComponent,
  accent,
  subModules,
  badges = {},
  metrics,
  actions,
}: {
  moduleKey: string;
  title: string;
  description: string;
  icon: Icon;
  accent: ModuleAccent;
  /** Already filtered to what the user may open. */
  subModules: SubModule[];
  /** Real figures by sub-module key. */
  badges?: Record<string, ReactNode>;
  /** Optional module-level figures above the cards. */
  metrics?: ReactNode;
  /** Optional quick actions beside the title. */
  actions?: ReactNode;
}) {
  const style = { '--mod': `var(--os-${accent})`, '--mod-soft': `var(--os-${accent}-soft)` } as CSSProperties;

  return (
    <div className="mx-auto w-full max-w-[1400px]">
      <Link
        to={ROUTES.dashboard}
        className="inline-flex items-center gap-1.5 rounded-md text-[13px] text-muted transition-colors hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--sms-accent-ring)]"
      >
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
        Back to Dashboard
      </Link>

      <header style={style} className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-4">
          <span
            aria-hidden="true"
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[var(--mod-soft)] text-[var(--mod)]"
          >
            <IconComponent className="h-6 w-6" />
          </span>
          <div className="min-w-0">
            <h1 className="text-[26px] leading-tight tracking-[-0.02em] text-ink sm:text-[28px]">{title}</h1>
            <p className="mt-1 max-w-[62ch] text-[14px] text-muted">{description}</p>
          </div>
        </div>
        {actions}
      </header>

      {metrics !== undefined && <div className="mt-6">{metrics}</div>}

      <section className="mt-7" aria-labelledby={`${moduleKey}-areas`}>
        <h2 id={`${moduleKey}-areas`} className="sr-only">
          {title} areas
        </h2>
        <ModuleGrid label={`${title} areas`} dense>
          {subModules.map((sub) => (
            <ModuleGridItem key={sub.key}>
              <ModuleCard
                id={`${moduleKey}-${sub.key}`}
                title={sub.title}
                description={sub.description}
                icon={sub.icon}
                to={sub.path}
                accent={accent}
                badge={badges[sub.key]}
              />
            </ModuleGridItem>
          ))}
        </ModuleGrid>
      </section>
    </div>
  );
}
